package org.chiangmeng.qfast.core.ui.staff

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Build
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Store
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import org.chiangmeng.qfast.core.domain.models.AppTheme
import org.chiangmeng.qfast.features.auth.AuthScreen

private data class SettingsTabItem(val title: String, val icon: ImageVector)

data class SettingsScreen(val state: StaffContract.State, val onIntent: (StaffContract.Intent) -> Unit) : Screen {
    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow

        LaunchedEffect(state.isAccountDeleted) {
            if (state.isAccountDeleted) {
                navigator.replaceAll(AuthScreen())
            }
        }

        var selectedTabIndex by remember { mutableStateOf(0) }
        val tabs = listOf(
            SettingsTabItem("ตั้งค่าร้าน", Icons.Default.Store),
            SettingsTabItem("จัดการคิว", Icons.Default.Build)
        )

        Scaffold(
            topBar = {
                TopAppBar(
                    title = { Text("ตั้งค่าระบบ") },
                    navigationIcon = {
                        IconButton(onClick = { navigator.pop() }) {
                            Icon(
                                imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                                contentDescription = "Back"
                            )
                        }
                    }
                )
            }
        ) { paddingValues ->
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
            ) {
                TabRow(selectedTabIndex = selectedTabIndex) {
                    tabs.forEachIndexed { index, item ->
                        Tab(
                            selected = selectedTabIndex == index,
                            onClick = { selectedTabIndex = index },
                            text = { Text(item.title, fontSize = 12.sp) },
                            icon = { Icon(item.icon, contentDescription = null) }
                        )
                    }
                }
                Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                    when (selectedTabIndex) {
                        0 -> ShopInfoTab(state, onIntent)
                        1 -> QueueManagementTab(state, onIntent)
                    }
                }
            }
        }
    }

    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    private fun ShopInfoTab(state: StaffContract.State, onIntent: (StaffContract.Intent) -> Unit) {
        val navigator = LocalNavigator.currentOrThrow
        val isShopActive = state.shop?.isActive == true
        var name by remember { mutableStateOf(state.shop?.name ?: "") }
        var adText by remember { mutableStateOf(state.shop?.adText ?: "") }
        var menuUrl by remember { mutableStateOf(state.shop?.menuUrl ?: "") }
        var openTime by remember { mutableStateOf(state.shop?.openTime ?: "") }
        var closeTime by remember { mutableStateOf(state.shop?.closeTime ?: "") }
        var selectedTheme by remember { mutableStateOf(state.shop?.theme ?: "AMBER") }

        val defaultCategories = mapOf("A" to "1-2 คน", "B" to "2-3 คน", "C" to "3-4 คน", "D" to "5 คนขึ้นไป")
        var currentCategories by remember(state.shop?.categories) {
            mutableStateOf((state.shop?.categories ?: defaultCategories).toMutableMap())
        }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Text("ข้อมูลร้านค้า", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)

            OutlinedTextField(
                value = name,
                onValueChange = { name = it },
                label = { Text("ชื่อร้าน") },
                modifier = Modifier.fillMaxWidth()
            )
            OutlinedTextField(
                value = adText,
                onValueChange = { adText = it },
                label = { Text("ข้อความประชาสัมพันธ์") },
                modifier = Modifier.fillMaxWidth()
            )
            OutlinedTextField(
                value = menuUrl,
                onValueChange = { menuUrl = it },
                label = { Text("URL เมนูอาหาร") },
                modifier = Modifier.fillMaxWidth()
            )
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                OutlinedTextField(
                    value = openTime,
                    onValueChange = { openTime = it },
                    label = { Text("เวลาเปิด") },
                    modifier = Modifier.weight(1f)
                )
                OutlinedTextField(
                    value = closeTime,
                    onValueChange = { closeTime = it },
                    label = { Text("เวลาปิด") },
                    modifier = Modifier.weight(1f)
                )
            }

            Text("ธีมของร้าน", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
            LazyRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                contentPadding = PaddingValues(vertical = 8.dp)
            ) {
                items(AppTheme.entries) { theme ->
                    val isSelected = selectedTheme == theme.name
                    val themeColor = when (theme) {
                        AppTheme.AMBER -> Color(0xFFFFB300)
                        AppTheme.OCEAN_BLUE -> Color(0xFF0288D1)
                        AppTheme.NATURE_GREEN -> Color(0xFF2E7D32)
                        AppTheme.ROYAL_PURPLE -> Color(0xFF7B1FA2)
                        AppTheme.MIDNIGHT -> Color(0xFF90A4AE)
                    }
                    Box(
                        modifier = Modifier
                            .size(50.dp)
                            .clip(CircleShape)
                            .background(themeColor)
                            .clickable { selectedTheme = theme.name },
                        contentAlignment = Alignment.Center
                    ) {
                        if (isSelected) {
                            Icon(
                                imageVector = Icons.Default.Check,
                                contentDescription = null,
                                tint = Color.White,
                                modifier = Modifier.size(24.dp)
                            )
                        }
                    }
                }
            }

            Card(
                modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f))
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Column(modifier = Modifier.weight(1f).padding(end = 16.dp)) {
                        Text(
                            text = "สถานะการเปิดร้าน",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold
                        )
                        Spacer(modifier = Modifier.height(2.dp))
                        Text(
                            text = if (isShopActive) "เปิดรับคิวปกติ" else "ปิดรับคิว",
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.Medium,
                            color = if (isShopActive) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.error
                        )
                    }

                    Switch(
                        checked = isShopActive,
                        onCheckedChange = { checked ->
                            onIntent(StaffContract.Intent.UpdateShopStatus(checked))
                        }
                    )
                }
            }

            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
            Text("จัดการหมวดหมู่คิว (A - Z)", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)

            currentCategories.entries.sortedBy { it.key }.forEach { entry ->
                val cat = entry.key
                val desc = entry.value
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Card(
                        modifier = Modifier.size(48.dp),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.primaryContainer)
                    ) {
                        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            Text(cat, fontWeight = FontWeight.Black, fontSize = 20.sp, color = MaterialTheme.colorScheme.primary)
                        }
                    }
                    OutlinedTextField(
                        value = desc,
                        onValueChange = { newValue -> if (newValue.length <= 20) currentCategories = currentCategories.toMutableMap().apply { put(cat, newValue) } },
                        label = { Text("รายละเอียด (ไม่เกิน 20 อักษร)") },
                        modifier = Modifier.weight(1f),
                        singleLine = true,
                        supportingText = { Text("${desc.length}/20") }
                    )
                    IconButton(
                        onClick = {
                            if (currentCategories.size > 1) {
                                currentCategories = currentCategories.toMutableMap().apply { remove(cat) }
                            }
                        },
                        enabled = currentCategories.size > 1
                    ) {
                        Icon(Icons.Default.Close, contentDescription = "Remove Category", tint = MaterialTheme.colorScheme.error)
                    }
                }
            }

            val availableLetters = ('A'..'Z').map { it.toString() }.filter { !currentCategories.containsKey(it) }
            if (availableLetters.isNotEmpty()) {
                var selectedNewLetter by remember { mutableStateOf(availableLetters.first()) }
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Text("เพิ่มหมวด:", fontWeight = FontWeight.Bold)
                    LazyRow(
                        modifier = Modifier.weight(1f),
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        items(availableLetters) { letter ->
                            FilterChip(
                                selected = selectedNewLetter == letter,
                                onClick = { selectedNewLetter = letter },
                                label = { Text(letter) }
                            )
                        }
                    }
                    Button(onClick = {
                        currentCategories = currentCategories.toMutableMap().apply { put(selectedNewLetter, "") }
                        val nextLetter = availableLetters.filter { it != selectedNewLetter }.firstOrNull() ?: "A"
                        selectedNewLetter = nextLetter
                    }) {
                        Text("เพิ่ม")
                    }
                }
            }

            Button(
                onClick = {
                    onIntent(StaffContract.Intent.UpdateCategories(currentCategories))
                    onIntent(StaffContract.Intent.UpdateShopInfo(name, adText, menuUrl, openTime, closeTime, selectedTheme))
                    navigator.pop()
                },
                modifier = Modifier.fillMaxWidth().height(56.dp)
            ) {
                Text("บันทึกข้อมูลร้าน", fontSize = 18.sp, fontWeight = FontWeight.Bold)
            }

            Spacer(modifier = Modifier.height(8.dp))

            OutlinedButton(
                onClick = {
                    onIntent(StaffContract.Intent.SignOut {
                        navigator.replaceAll(AuthScreen())
                    })
                },
                modifier = Modifier.fillMaxWidth().height(48.dp)
            ) {
                Text("ออกจากระบบ (Sign Out)")
            }
        }
    }

    @Composable
    private fun QueueManagementTab(state: StaffContract.State, onIntent: (StaffContract.Intent) -> Unit) {
        val navigator = LocalNavigator.currentOrThrow
        var showDeleteDialog by remember { mutableStateOf(false) }

        if (showDeleteDialog) {
            AlertDialog(
                onDismissRequest = { showDeleteDialog = false },
                title = { Text("ยืนยันการลบบัญชีผู้ใช้") },
                text = { Text("การลบบัญชีจะทำให้ข้อมูลร้านค้าและคิวทั้งหมดถูกลบถาวร ไม่สามารถเรียกคืนได้") },
                confirmButton = {
                    TextButton(
                        onClick = {
                            showDeleteDialog = false
                            onIntent(StaffContract.Intent.DeleteAccount {
                                navigator.replaceAll(AuthScreen())
                            })
                        },
                        colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.error)
                    ) {
                        Text("ลบบัญชี")
                    }
                },
                dismissButton = {
                    TextButton(onClick = { showDeleteDialog = false }) {
                        Text("ยกเลิก")
                    }
                }
            )
        }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Card(
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer),
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(
                    modifier = Modifier.padding(16.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Icon(
                        imageVector = Icons.Default.Warning,
                        contentDescription = "Warning",
                        tint = MaterialTheme.colorScheme.error,
                        modifier = Modifier.size(48.dp)
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    Text(
                        text = "คำเตือน",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onErrorContainer
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = "การรีเซ็ตคิวจะทำให้คิวทั้งหมดถูกลบออกจากระบบ",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onErrorContainer
                    )
                }
            }
            Spacer(modifier = Modifier.height(32.dp))
            Button(
                onClick = {
                    onIntent(StaffContract.Intent.ResetQueue)
                    navigator.pop()
                },
                modifier = Modifier.fillMaxWidth().height(64.dp),
                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
            ) {
                Text(
                    text = "ยืนยันการล้างคิว",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
            }
            Spacer(modifier = Modifier.height(16.dp))
            OutlinedButton(
                onClick = { showDeleteDialog = true },
                modifier = Modifier.fillMaxWidth().height(56.dp),
                colors = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.error)
            ) {
                Text("ลบบัญชีผู้ใช้ (Delete Account)")
            }
        }
    }
}
